import { IFilters, IPaginationParams, TSortingParams, TSortingResults, TFiltersResults } from '@typedefs/api';
import { TContractorFilterFields, TContractorSortingFields } from '@typedefs/contractors';
import { AllFilterFields, AllSortingFields } from '@typedefs/utils';
import { ref, Ref, unref } from 'vue';
import { DataTableColumn } from 'vuestic-ui';

export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/** Validation */
export const validators = {
  email: (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(v) || 'Please enter a valid email address';
  },
  mobileNumber: (v: string) => {
    // start with 8 or 9, followed by 7 more numbers
    const pattern = /^[89]\d{7}$/;
    return pattern.test(v) || 'Please enter a valid mobile number';
  },
  nricOrFIN: (v: string) => {
    // valid nric or fin format, start with S,T,F,G,M followed by 7 more numbers and an alphabet
    const pattern = /^[STFGM]\d{7}[A-Z]$/;
    return pattern.test(v) || 'Please enter a valid NRIC or FIN number';
  },
  nric: (v: string) => {
    // SG locals' nric format, start with S or T followed by 7 more numbers and an alphabet
    const pattern = /^[ST]\d{7}[A-Z]$/;
    return pattern.test(v);
  },

  required: (v: any) => !!v || 'This field is required',

  noSpecialCharacters: (v: string) => {
    if (!v) return true; // Allow empty values (use required validator separately)
    const blacklistRegex = /[<>'";&(){}[\]\\|`~#?]/;
    return !blacklistRegex.test(v) || 'No symbols are allowed in the name';
  },
};

export const timeStringToDateObj = (timeString: string) => {
  const [hours, minutes] = timeString.split(':');
  const dateObj = new Date();
  dateObj.setHours(Number(hours));
  dateObj.setMinutes(Number(minutes));
  dateObj.setSeconds(0);
  dateObj.setMilliseconds(0);
  return dateObj;
};

export const dateObjToTimeString = (dateObj: Date) => {
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const seconds = '00'; // just default seconds to 00
  return `${hours}:${minutes}:${seconds}`;
};

// returned schedule name in stringified of "[\"G9012H\"]"
export const formatScheduleName = (schedule: string | string[]) => {
  if (Array.isArray(schedule)) {
    return schedule.join(', ');
  }

  try {
    const parsed = JSON.parse(schedule);
    return Array.isArray(parsed) ? parsed.join(', ') : schedule;
  } catch {
    return schedule || '';
  }
};

export const generateRandomStr = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const stringLength = 32;
  let randomString = '';

  for (let i = 0; i < stringLength; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomString += characters[randomIndex];
  }
  return randomString;
};

export const toSnakeCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Convert camelCase to snake_case
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2') // Handle PascalCase and mixed cases
    .toLowerCase(); // Convert everything to lowercase
};

export const createHeaders = (token: string | null, contentType: string = 'application/json'): Headers => {
  const headers = new Headers();
  headers.append('Content-Type', contentType);

  if (token !== null) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  return headers;
};

export interface IHTTPMethodOptions {
  token?: string | null;
  stringify?: boolean;
  contentType?: string;
  signal?: AbortSignal;
}

export const get = async <T>(url: string, { token = '' }: Partial<IHTTPMethodOptions>) => {
  const options: RequestInit = {
    method: 'GET',
  };

  console.log('GET-ing', url);
  options.headers = createHeaders(token);

  try {
    const response = await fetch(url, options);

    if (response.status !== 200) {
      const result = await response.text();
      throw new Error(`${response.status} - ${result}`);
    }

    const results: T = await response.json();
    return results;
  } catch (e) {
    console.log(`could not GET ${url}`);
    console.error(e);
    throw e;
  }
};

export const post = async (url: string, body: any, { token = '' }: IHTTPMethodOptions): Promise<any> => {
  const options: RequestInit = {
    method: 'POST',
    body: JSON.stringify(body),
  };

  options.headers = createHeaders(token);

  try {
    const response = await fetch(url, options);

    console.log(132, response);

    if (response.status !== 200) {
      const result = await response.text();
      throw new Error(`${response.status} - ${result}`);
    }

    const results = await response.json();
    return results;
  } catch (e) {
    console.log(`could not POST to ${url}`);
    console.error(e);
    throw e;
  }
};

export const httpDelete = async (url: string, { token = '' }: IHTTPMethodOptions, body: any | null = null) => {
  const options: RequestInit = {
    method: 'DELETE',
  };

  options.headers = createHeaders(token);

  if (body !== null) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);

    if (response.status !== 200) {
      const result = await response.text();
      throw new Error(`${response.status} - ${result}`);
    } else {
      return true;
    }
  } catch (e) {
    console.log(`could not DELETE ${url}`);
    console.error(e);
    throw e;
  }
};

export const put = async (
  url: string,
  body: any,
  { token = '', stringify = true, contentType = 'application/json' }: IHTTPMethodOptions,
) => {
  const options: RequestInit = {
    method: 'PUT',
  };

  if (stringify) {
    options.body = JSON.stringify(body);
  } else {
    options.body = body;
  }

  options.headers = createHeaders(token, contentType);

  try {
    const response = await fetch(url, options);

    if (response.status !== 200) {
      const result = await response.text();
      throw new Error(`${response.status} - ${result}`);
    }

    if (stringify) {
      const results = await response.json();
      return results;
    } else {
      return response;
    }
  } catch (e) {
    console.log(`could not PUT into ${url}`);
    console.error(e);
    throw e;
  }
};

export const matchPaths = (path: string, allowedPaths: string[]): boolean => {
  let allowed = false;
  const pathParts = path.split('/');

  for (let x = 0; x < allowedPaths.length; x++) {
    const allowedPath = allowedPaths[x];
    const allowedPathParts = allowedPath.split('/');

    if (allowedPathParts.length !== pathParts.length) {
      continue;
    }

    for (let y = 0; y < allowedPathParts.length; y++) {
      const currentPathPart = pathParts[y];
      const currentAllowedPathPart = allowedPathParts[y];

      if (currentPathPart !== currentAllowedPathPart && currentAllowedPathPart !== '*') {
        break;
      } else if (
        (currentAllowedPathPart === currentPathPart || currentAllowedPathPart === '*') &&
        y === allowedPathParts.length - 1
      ) {
        allowed = true;
      }
    }

    // for (let y = 0; y < pathParts.length; y++) {
    //   const currentPart = pathParts[y];
    //   // towncouncil, contractors
    //   if (allowedPathParts[y] !== '*' && allowedPathParts[y] !== pathParts[y]) {
    //     break;
    //   } else if (allowedPathParts[y] === pathParts[y]) {
    //     allowed = true;
    //     break;
    //   }

    //   allowed = true;

    //   // if (allowedPathParts[y] !== pathParts[y] && allowedPathParts[y] !== '*') {
    //   //   continue;
    //   // } else if (allowedPathParts[y] === pathParts[y] || allowedPathParts[y] === '*') {
    //   //   allowed = true;
    //   //   break;
    //   // }
    // }
  }

  return allowed;
};

export const convertSortingResultsIntoRef = <K extends AllSortingFields>(sorting: K): Ref<TSortingParams<K>> => {
  const sortingRef = ref<TSortingParams<K>>({
    order: sorting.Order,
    'order-by': sorting.OrderBy,
  });
  return sortingRef;
};

export const convertFilterResultsIntoRef = <K extends AllFilterFields>(filters: K): Ref<IFilters<K>> => {
  const filtersRef = ref<IFilters<K>>({
    isActive: false,
    query: [],
  });

  if (filters) {
    filtersRef.value.isActive = true;
    filtersRef.value.query = Object.entries(filters).map(
      ([key, value]) =>
        ({
          key,
          value,
        }) as {
          key: K;
          value: string;
        },
    );
  }
  return filtersRef;
};

export const updateRefWithResults = <T extends AllFilterFields, K extends AllSortingFields>({
  sorting,
  filters,
  sortingRef,
  filtersRef,
}: {
  filters?: Partial<TFiltersResults<T>>;
  filtersRef: Ref<IFilters<T>>;
  sorting?: TSortingResults<K>;
  sortingRef: Ref<TSortingParams<K>>;
}) => {
  if (sorting) {
    sortingRef.value = {
      order: sorting.Order,
      'order-by': sorting.OrderBy,
    };
  }

  if (filters) {
    filtersRef.value.isActive = true;
    filtersRef.value.query = Object.entries(filters).map(
      ([key, value]) =>
        ({
          key,
          value,
        }) as {
          key: T;
          value: string;
        },
    );
  }
};

export const convertParamsRefsIntoURL = <T extends AllFilterFields, K extends AllSortingFields>({
  filters,
  pagination,
  sorting,
}: {
  filters: Ref<IFilters<T>>;
  pagination: Ref<IPaginationParams>;
  sorting: Ref<TSortingParams<K>>;
}): string => {
  const queryParams = convertParamstoQueryParams<TContractorFilterFields, TContractorSortingFields>({
    pagination: unref(pagination),
    filters: unref(filters),
    sorting: unref(sorting),
  });

  const url = queryParams.toString();
  return url;
};

export const convertParamstoQueryParams = <T extends string, K extends string>({
  filters,
  pagination,
  sorting,
}: {
  filters: IFilters<T>;
  pagination: IPaginationParams;
  sorting: TSortingParams<K>;
}): URLSearchParams => {
  const params: Record<string, string | number> = {
    ...sorting,
  };

  if (sorting.order === null) {
    delete params.order;
  }

  if (pagination.n) {
    params.n = pagination.n;
  }

  if (pagination.CurrentPage) {
    params.page = pagination.CurrentPage;
  }

  const queryParam = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    queryParam.append(key, String(value));
  }

  if (filters && filters.isActive && filters.query) {
    for (const query of Object.values(filters.query)) {
      if (query && query.value && query.value.length !== 0) {
        queryParam.append(query.key, String(query.value));
      }
    }
  }

  return queryParam;
};

export const makeDefaultPagination = (n = 10): IPaginationParams => {
  return {
    CurrentPage: 1,
    TotalPages: 1,
    n,
    TotalResults: 0,
  };
};

export const makeDefaultPaginationRef = (n = 10): Ref<IPaginationParams> => ref(makeDefaultPagination(n));

export function makeDefaultFilters<T extends AllFilterFields>(queries?: Partial<Record<T, string>>) {
  const filters: any = {
    isActive: false,
    query: [],
  };

  if (queries) {
    filters.isActive = true;
    for (const [key, value] of Object.entries(queries)) {
      filters.query.push({
        key: key as T,
        value,
      });
    }
  }

  return filters as IFilters<T>;
}

export function makeDefaultFiltersRef<T extends AllFilterFields>(queries?: Partial<Record<T, any>>): Ref<IFilters<T>> {
  return ref(makeDefaultFilters(queries));
}

export const getFlooredTimeByMinuteStep = (date: Date, stepMinutes = 5): Date => {
  const floored = new Date(date);
  const minutes = floored.getMinutes();
  floored.setMinutes(minutes - (minutes % stepMinutes));
  return new Date(floored);
};

export const getNewScheduleStartTime = (date: Date): Date => {
  return getFlooredTimeByMinuteStep(date);
};

export const getNewScheduleEndTime = (date: Date): Date => {
  const startTime = new Date(getFlooredTimeByMinuteStep(date));
  return new Date(startTime.setHours(startTime.getHours() + 1));
};

export function convertColumnsToTableFilters<T>(acceptedKeys: T[], columns: DataTableColumn<T>[]) {
  return columns
    .filter((column) => acceptedKeys.indexOf(column.key) > -1)
    .map((column) => ({
      label: column.label ?? column.key,
      key: column.name ?? column.key, // prioritize use column name if available
      type: column.type ?? 'text',
    }));
}
